import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  TextField,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  ArrowBack,
  ShoppingCart,
  Payment,
  LocalDining,
  Add,
  Remove,
  Delete,
  QrCode,
  CheckCircle,
  Store,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import UserPageLayout from '../../components/user/UserPageLayout';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useNotification } from '../../contexts/NotificationContext';
import { orderService } from '../../services/orderService';
import Footer from '../../components/user/Footer';
import { getImageProps } from '../../utils/imageUtils';


const url = window.location.href;
const urlarr = url.split('/')
const tableString = urlarr[(urlarr.length)-1]

const TableCheckoutPage = () => {
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  
  const { 
    cart, 
    getCartTotal, 
    clearCart,
    updateCartItem,
    removeFromCart,
    selectedOutlet,
    isDineInAvailable,
    isDeliveryAvailable,
    isTakeawayAvailable
  } = useRestaurant();

  const tableInfo = location.state || {};

  // Debug table info
  // console.log('Table Info:', tableInfo);
  // console.log('Table DB ID:', tableInfo.table_id);

  // Calculate order summary
  const summary = {
    subtotal: getCartTotal ? getCartTotal() : 0,
    tax_amount: Math.round((getCartTotal ? getCartTotal() : 0) * 0.18),
    total_amount: (getCartTotal ? getCartTotal() : 0) + Math.round((getCartTotal ? getCartTotal() : 0) * 0.18),
  };

  const handleQuantityChange = (cartItemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(cartItemId);
    } else {
      updateCartItem(cartItemId, { quantity: newQuantity });
    }
  };

  const handleRemoveItem = (cartItemId) => {
    removeFromCart(cartItemId);
  };

  const validatePhoneNumber = (phone) => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    // Check if it's a valid phone number (8-15 digits)
    return cleaned.length >= 8 && cleaned.length <= 15;
  };

  const validateForm = () => {
    const errors = [];
    
    if (!cart || cart.length === 0) {
      errors.push('Your cart is empty. Please add items before placing an order.');
    }
    
    if (!customerName.trim()) {
      errors.push('Full name is required');
    }
    
    if (!customerPhone.trim()) {
      errors.push('Phone number is required');
    } else if (!validatePhoneNumber(customerPhone)) {
      errors.push('Please enter a valid phone number (8-15 digits)');
    }
    
    // Check if table ID is available
    if (!tableInfo.tableDbId && !tableInfo.table_id) {
      errors.push('Table information is missing. Please go back and select a table again.');
    }
    
    // Check dine-in availability
    if (!isDineInAvailable()) {
      errors.push('Dine-in service is not available at this outlet. Please choose delivery or takeaway instead.');
    }
    
    return errors;
  };

  const handlePlaceOrder = async () => {

    console.log("Cart => ");
    
    const errors = validateForm();
    if (errors.length > 0) {
      showError(errors.join('\n'));
      return;
    }

    setSubmitting(true);
    try {
      // Create table order
      const tableId = tableInfo.tableDbId || tableInfo.table_id;
      const parsedTableId = tableId ? parseInt(tableId) : null;
      
      if (!parsedTableId || isNaN(parsedTableId)) {
        showError('Invalid table information. Please go back and select a table again.');
        setSubmitting(false);
        return;
      }

      const orderPayload = {
        outlet_id: selectedOutlet?.id,
        table_id: parsedTableId,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail || undefined,
        special_instructions: specialInstructions || undefined,
        items: cart.map(ci => ({
          menu_item_id: ci.id,
          quantity: ci.quantity || 1,
          size_name: null, // Not used in current implementation
          size_price: 0,
          base_price: ci.base_price || 0,
          total_price: (ci.price || ci.base_price || 0) * (ci.quantity || 1),
          special_instructions: ci.special_instructions || undefined,
          addons: (ci.addons || []).map(a => ({ 
            addon_id: a.id, 
            quantity: a.quantity || 1, 
            price: a.price 
          }))
        })),
        subtotal: summary.subtotal,
        tax_amount: summary.tax_amount,
        total_amount: summary.total_amount,
        order_type: 'dine_in',
      };

      // console.log('Order Payload:', orderPayload);
      // console.log('Table ID being sent:', orderPayload.table_id);
      // console.log('Table ID type:', typeof orderPayload.table_id);

      const order = await orderService.createTableOrder(orderPayload);
      
      setCreatedOrder(order);
      setOrderCreated(true);
      showSuccess('Order placed successfully!');

      if (paymentMethod === 'online') {
        // Process online payment
        try {
          const payment = await orderService.createHitpayPayment(order.id);
          if (payment?.checkout_url) {
            // Store order details for payment result page
            sessionStorage.setItem('pendingOrder', JSON.stringify({
              id: order.id,
              order_number: order.order_number,
              total_amount: order.total_amount,
              order_type: 'dine_in',
              table_id: tableInfo.tableId
            }));
            window.location.href = payment.checkout_url;
          } else {
            showError('Payment creation failed. Please try again.');
          }
        } catch (error) {
          console.error('Payment creation error:', error);
          showError('Payment creation failed. Please try again.');
        }
      } else {
        // Cash payment - order is complete
        showSuccess('Order placed successfully! You can pay at the counter when ready.');
        clearCart();
      }
    } catch (error) {
      console.error('Order creation error:', error);
      showError('Failed to place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToMenu = () => {
    navigate('/table-menu', { 
      state: { 
        tableId: tableInfo.tableId,
        tableType: tableInfo.tableType
      } 
    });
  };

  const handleBackToTable = () => {
    navigate('/table-reservations', { 
      state: { 
        tableId: tableInfo.tableId,
        tableType: tableInfo.tableType
      } 
    });
  };

  if (!cart || cart.length === 0) {
    return (
      <Box sx={{ 
        bgcolor: 'background.default', 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <UserPageLayout
          title="Empty Cart"
          subtitle="Add items to your cart to continue"
          showBackButton
          onBack={handleBackToMenu}
      >
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ShoppingCart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Your cart is empty
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add some delicious items from our menu to get started
          </Typography>
          <Button
            variant="contained"
            onClick={handleBackToMenu}
            startIcon={<LocalDining />}
            sx={{ borderRadius: 2 }}
          >
            Browse Menu
          </Button>
        </Box>
        </UserPageLayout>
        
        {/* Footer */}
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <UserPageLayout
        title="Table Checkout"
        subtitle={`Complete your order for table ${tableInfo.tableId || ''}`}
        showBackButton
        onBack={handleBackToMenu}
    >
      {/* Table Info Header */}
      {!selectedOutlet ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Store sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
            <Typography variant="h6" color="warning.main" gutterBottom>
              No Outlet Selected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please select an outlet to proceed with your order
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/table-reservations')}
              startIcon={<ArrowBack />}
            >
              Back to Table Selection
            </Button>
          </Box>
        </Paper>
      ) : !isDineInAvailable() ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" color="error.main" gutterBottom>
                Dine-in Service Unavailable
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dine-in service is currently unavailable at {selectedOutlet.name}. 
                Please choose delivery or takeaway instead.
              </Typography>
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/checkout', { state: { orderType: 'delivery' } })}
                disabled={!isDeliveryAvailable()}
              >
                Order Delivery
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/checkout', { state: { orderType: 'takeaway' } })}
                disabled={!isTakeawayAvailable()}
              >
                Order Takeaway
              </Button>
            </Box>
          </Box>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600} color="primary.main">
              📍 Table {tableInfo.tableId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ready to complete your order
            </Typography>
          </Box>
          
          <Chip 
            label={`${cart.length} items`}
            color="primary"
            variant="outlined"
          />
        </Box>
        </Paper>
      )}

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Order Items */}
        <Grid item xs={12} lg={8}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Your Order Items
            </Typography>
            
            <List>
              {cart.map((item, index) => (
                <ListItem key={index} sx={{ px: 0, py: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box sx={{ display: 'flex', width: '100%', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
                    <CardMedia
                      component="img"
                      image={getImageProps(item.image_url || item.image, 'menu_items').src}
                      alt={item.name}
                      sx={{ 
                        width: { xs: 60, sm: 80 }, 
                        height: { xs: 60, sm: 80 }, 
                        borderRadius: 2, 
                        mr: { xs: 0, sm: 2 },
                        mb: { xs: 1, sm: 0 }
                      }}
                    />
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {item.name}
                        </Typography>
                        {item.selectedSize && (
                          <Chip label={item.selectedSize.name} size="small" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        {item.selectedAddons && item.selectedAddons.length > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Add-ons: {item.selectedAddons.map(a => `${a.name} (${a.quantity})`).join(', ')}
                          </Typography>
                        )}
                        {item.specialInstructions && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Special: {item.specialInstructions}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      width: { xs: '100%', sm: 'auto' },
                      mt: { xs: 1, sm: 0 }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, (item.quantity || 1) - 1)}
                          disabled={!item.quantity || item.quantity <= 1}
                        >
                          <Remove />
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: 30, textAlign: 'center' }}>
                          {item.quantity || 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, (item.quantity || 1) + 1)}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                      
                      {/* Price Display with Discount */}
                      <Box>
                        {item?.discount_enabled && item?.discount_percentage > 0 ? (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  textDecoration: 'line-through', 
                                  color: 'text.secondary' 
                                }}
                              >
                                S${(item.original_price * item.quantity).toFixed(2)}
                              </Typography>
                              <Chip
                                label={`${item.discount_percentage}% OFF`}
                                size="small"
                                color="error"
                                sx={{ 
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  height: 18
                                }}
                              />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="primary.main">
                              S${((item.price || item.base_price || 0) * (item.quantity || 1)).toFixed(2)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="h6" fontWeight={600} color="primary.main">
                            S${((item.price || item.base_price || 0) * (item.quantity || 1)).toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                      
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(item.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Order Summary & Payment */}
        <Grid item xs={12} lg={4}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Order Summary
            </Typography>
            
            {orderCreated && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  ✅ Order #{createdOrder?.order_number} Placed Successfully!
                </Typography>
                <Typography variant="caption">
                  {paymentMethod === 'online' 
                    ? 'Redirecting to payment...'
                    : 'You can pay at the counter when ready.'
                  }
                </Typography>
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal</Typography>
                <Typography>S${summary.subtotal.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Taxes (18%)</Typography>
                <Typography>S${summary.tax_amount.toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={800}>Total</Typography>
                <Typography variant="h6" fontWeight={800} color="primary.main">
                  S${summary.total_amount.toFixed(2)}
                </Typography>
              </Box>
            </Box>

            {!orderCreated && (
              <>
                {/* Customer Details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Customer Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name *"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number *"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        size="small"
                        type="tel"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email (Optional)"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        size="small"
                        type="email"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Special Instructions (Optional)"
                        value={specialInstructions}
                        onChange={(e) => setSpecialInstructions(e.target.value)}
                        multiline
                        rows={2}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Payment Method */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Payment Method
                  </Typography>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      sx={{ 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 2 }
                      }}
                    >
                      <FormControlLabel 
                        value="online" 
                        control={<Radio />} 
                        label="Pay Online (HitPay)" 
                        sx={{ 
                          border: '1px solid',
                          borderColor: paymentMethod === 'online' ? 'primary.main' : 'grey.300',
                          borderRadius: 2,
                          p: 1,
                          m: 0,
                          bgcolor: paymentMethod === 'online' ? 'primary.50' : 'transparent'
                        }}
                      />
                      <FormControlLabel 
                        value="cash" 
                        control={<Radio />} 
                        label="Pay at Counter" 
                        sx={{ 
                          border: '1px solid',
                          borderColor: paymentMethod === 'cash' ? 'primary.main' : 'grey.300',
                          borderRadius: 2,
                          p: 1,
                          m: 0,
                          bgcolor: paymentMethod === 'cash' ? 'primary.50' : 'transparent'
                        }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* Place Order Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePlaceOrder}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : <Payment />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                >
                  {submitting ? 'Placing Order...' : 'Place Order'}
                </Button>
              </>
            )}

            {orderCreated && (
              <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleBackToTable}
                  startIcon={<QrCode />}
                  sx={{ borderRadius: 2 }}
                >
                  Back to Table
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleBackToMenu}
                  startIcon={<LocalDining />}
                  sx={{ borderRadius: 2 }}
                >
                  Order More
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </UserPageLayout>
    
    {/* Footer */}
    <Footer />
  </Box>
  );
};

export default TableCheckoutPage; 